import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { ArrowRight, Calculator, Home } from 'lucide-react';

/* Pitch factor table (rise/12 → multiplier) — matches the pitch calculator. */
const PITCH_FACTORS = {
  0: 1.0, 1: 1.003, 2: 1.014, 3: 1.031, 4: 1.054, 5: 1.083, 6: 1.118,
  7: 1.158, 8: 1.202, 9: 1.25, 10: 1.302, 11: 1.357, 12: 1.414,
  13: 1.474, 14: 1.537, 15: 1.601, 16: 1.667, 17: 1.734, 18: 1.803,
};

export default function RoofSquaresCalculator() {
  const { openAuthModal } = useAuth();
  const [area, setArea] = useState('');
  const [pitch, setPitch] = useState('4');
  const [waste, setWaste] = useState('10');

  const r = useMemo(() => {
    const footprint = Number(area) || 0;
    if (footprint <= 0) return null;
    const factor = PITCH_FACTORS[parseInt(pitch)] ?? 1;
    const roofArea = footprint * factor;
    const squares = roofArea / 100;
    const w = (Number(waste) || 0) / 100;
    const bundles = Math.ceil(squares * 3 * (1 + w));        // 3 bundles per square
    const underlaymentRolls = Math.ceil(squares / 4);         // 15lb felt ~4 squares/roll
    const nailsLbs = Math.ceil(squares * 2.5);                // ~2.5 lb/square
    return {
      roofArea: Math.round(roofArea),
      squares: Math.round(squares * 10) / 10,
      bundles, underlaymentRolls, nailsLbs,
      factor,
    };
  }, [area, pitch, waste]);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Roofing Squares Calculator | How Many Squares Is My Roof? | SquareCount</title>
        <meta name="description" content="Free roofing squares calculator: enter your roof area and pitch to get roofing squares, shingle bundles (with waste), underlayment, and nails. 1 square = 100 sq ft. Or measure your roof automatically from satellite with SquareCount." />
        <meta property="og:title" content="Roofing Squares Calculator | SquareCount" />
        <meta property="og:description" content="Convert roof area + pitch into squares, shingle bundles, underlayment, and nails — free." />
        <link rel="canonical" href="https://squarecount.io/tools/roof-squares-calculator" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'WebApplication',
          name: 'Roofing Squares Calculator', applicationCategory: 'BusinessApplication', operatingSystem: 'Web',
          url: 'https://squarecount.io/tools/roof-squares-calculator',
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
          description: 'Convert roof area and pitch into roofing squares, shingle bundles, underlayment, and nails.',
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org', '@type': 'FAQPage',
          mainEntity: [
            { '@type': 'Question', name: 'How many square feet are in a roofing square?', acceptedAnswer: { '@type': 'Answer', text: 'One roofing square equals 100 square feet of roof surface. To find your squares, divide your total (pitch-adjusted) roof area in square feet by 100.' } },
            { '@type': 'Question', name: 'How many shingle bundles are in a square?', acceptedAnswer: { '@type': 'Answer', text: 'Most architectural shingles come 3 bundles per square. So a 20-square roof needs about 60 bundles, plus a waste allowance (typically 10%).' } },
            { '@type': 'Question', name: 'How do I calculate roofing squares from satellite?', acceptedAnswer: { '@type': 'Answer', text: 'Measure the roof footprint from satellite imagery, apply the pitch factor for the slope, then divide by 100. SquareCount does this automatically in about 60 seconds and outputs squares, bundles, and a full material list.' } },
          ],
        })}</script>
      </Helmet>
      <Navbar />

      <section className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/30 rounded-full px-4 py-2 mb-5">
            <Calculator className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-sm font-medium">Free tool</span>
          </div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mb-4">Roofing Squares Calculator</h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Enter your roof area and pitch to get roofing squares, shingle bundles, underlayment, and nails.
            One square = 100 sq ft. Don't know your roof area? SquareCount measures it from satellite in ~60 seconds.
          </p>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Roof footprint area (sq ft)</label>
              <div className="relative">
                <Home className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="number" inputMode="decimal" min="0" value={area} onChange={(e) => setArea(e.target.value)}
                  placeholder="e.g. 2000" data-testid="area-input"
                  className="w-full pl-9 pr-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 focus:ring-1 focus:ring-orange-500 outline-none" />
              </div>
              <p className="text-xs text-slate-500 mt-1">The flat (top-down) area — we apply the pitch factor for you.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Roof pitch</label>
              <select value={pitch} onChange={(e) => setPitch(e.target.value)} data-testid="pitch-input"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 outline-none bg-white">
                {Array.from({ length: 19 }, (_, i) => {
                  const hint = { 4: ' · common', 6: ' · typical', 8: ' · steep' }[i] || '';
                  return <option key={i} value={i}>{i}/12{hint}</option>;
                })}
              </select>
              <p className="text-xs text-slate-500 mt-1">Not sure? Most homes are 4/12–6/12.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Waste allowance (%)</label>
              <input type="number" inputMode="decimal" min="0" value={waste} onChange={(e) => setWaste(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-orange-500 outline-none" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200">
            <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">Estimate</h2>
            {r ? (
              <ul className="divide-y divide-slate-100">
                {[
                  ['Pitch-adjusted roof area', `${r.roofArea.toLocaleString()} sq ft`],
                  ['Roofing squares', r.squares],
                  ['Shingle bundles (incl. waste)', r.bundles],
                  ['Underlayment rolls (15 lb)', r.underlaymentRolls],
                  ['Roofing nails', `~${r.nailsLbs} lb`],
                ].map(([label, val]) => (
                  <li key={label} className="flex justify-between py-2 text-sm">
                    <span className="text-slate-600">{label}</span>
                    <span className="font-semibold text-slate-900">{val}</span>
                  </li>
                ))}
              </ul>
            ) : <p className="text-slate-400 text-sm">Enter your roof area to see squares and materials.</p>}
          </div>
        </div>

        <div className="mt-10 bg-slate-900 rounded-2xl p-8 text-center text-white">
          <h2 className="font-heading font-black text-2xl mb-3">Don't know your roof area?</h2>
          <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
            SquareCount measures any roof from satellite imagery in about 60 seconds — squares, pitch, waste,
            and a full material list, no tape measure required. Free to start.
          </p>
          <button onClick={() => openAuthModal('signup')} className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-heading font-bold tracking-wide px-8 py-3 rounded-lg">
            Measure My Roof Free <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-xs text-slate-400 mt-3">
            <Link to="/tools/gutter-calculator" className="underline hover:text-slate-200">Also try the gutter calculator →</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
